import type { Root, SavedView } from "../../domain/app-types.ts";
import type { Category } from "../../domain/entities.ts";
import { escapeHtml } from "../../ui/text.ts";
import { destinationKey, pruneDestinations, type DestinationRef } from "./destinations.ts";
import type { RecentTask } from "./qol-preferences.ts";

export interface DestinationSectionsState {
  roots: Root[];
  categories: Category[];
  savedViews: SavedView[];
  favoriteDestinations: DestinationRef[];
  recentDestinations: DestinationRef[];
  favoriteTaskIds: string[];
  recentTasks: RecentTask[];
  icon: (name: string) => string;
}

export interface DestinationSectionsResult {
  html: string;
  favoriteDestinations: DestinationRef[];
  recentDestinations: DestinationRef[];
}

function destinationLabel(destination: DestinationRef, state: Pick<DestinationSectionsState, "roots" | "categories" | "savedViews">): string {
  if (destination.kind === "space") return state.roots.find(root => root.id === destination.id)?.name || "Unavailable space";
  if (destination.kind === "collection") return state.categories.find(category => category.id === destination.id)?.name || "Unavailable collection";
  return state.savedViews.find(view => view.id === destination.id)?.name || "Unavailable view";
}

function destinationButton(destination: DestinationRef, state: DestinationSectionsState): string {
  const label = escapeHtml(destinationLabel(destination, state));
  if (destination.kind === "space") return `<button class="nav-button destination-button" data-root="${destination.id}" title="${label}">${state.icon("folder")}<span>${label}</span></button>`;
  if (destination.kind === "collection") return `<button class="nav-button destination-button" data-category="${destination.id}" data-parent-root="${destination.rootId || ""}" title="${label}">${state.icon("list")}<span>${label}</span></button>`;
  return `<button class="nav-button destination-button" data-saved-view="${destination.id}" title="${label}">${state.icon("filter")}<span>${label}</span></button>`;
}

export function destinationSectionsView(state: DestinationSectionsState): DestinationSectionsResult {
  const valid = new Set([
    ...state.roots.map(root => destinationKey({ kind: "space", id: root.id })),
    ...state.categories.map(category => destinationKey({ kind: "collection", id: category.id, rootId: category.rootId })),
    ...state.savedViews.map(view => destinationKey({ kind: "saved-view", id: view.id, rootId: view.rootId })),
  ]);
  const favoriteDestinations = pruneDestinations(state.favoriteDestinations, valid);
  const favoriteKeys = new Set(favoriteDestinations.map(destinationKey));
  const recentDestinations = pruneDestinations(state.recentDestinations, valid).filter(destination => !favoriteKeys.has(destinationKey(destination)));
  const favoriteTasks = state.favoriteTaskIds.flatMap(id => {
    const task = state.recentTasks.find(item => item.id === id);
    return task ? [task] : [];
  });
  const favorites = favoriteDestinations.length || favoriteTasks.length
    ? `<section class="destination-section"><div class="nav-section-title"><span>Favorites</span></div><nav aria-label="Favorite destinations">${favoriteDestinations.map(destination => destinationButton(destination, state)).join("")}${favoriteTasks.map(task => `<button class="nav-button destination-button" data-located-task="${task.id}">${state.icon("star")}<span>${escapeHtml(task.title)}</span></button>`).join("")}</nav></section>`
    : "";
  const recent = recentDestinations.length
    ? `<section class="destination-section destination-section--recent"><div class="nav-section-title"><span>Recent</span></div><nav aria-label="Recent destinations">${recentDestinations.slice(0, 3).map(destination => destinationButton(destination, state)).join("")}</nav></section>`
    : "";

  return { html: `${favorites}${recent}`, favoriteDestinations, recentDestinations };
}
